"use client";

import React, { Suspense, useState } from "react";
import { useQueryParam } from "@/lib/routing";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Badge } from "@/app/components/ui/badge";
import { Button } from "@/app/components/ui/button";
import { ScrollArea } from "@/app/components/ui/scroll-area";
import { Separator } from "@/app/components/ui/separator";
import { ExternalLink, Star, Truck, Award, ShoppingCart, Monitor, Shirt, Home, Book, Mountain, Sparkles, ShoppingBag, Car, Hammer, Gamepad2, Store, Tag, Search, Filter, BarChart3, Gift, Download, Heart, ArrowUpDown, SlidersHorizontal, Share2, Download as DownloadIcon, TrendingUp, Users, Zap } from "lucide-react";
import { cheapLifeSites, categories, getSitesByCategory, getFeaturedSites } from "@/app/data/cheap-life-sites";

const CATEGORIES = [
  { id: "featured", name: "Featured Deals", icon: "Star", description: "Top-rated shopping destinations" },
  { id: "electronics", name: "Electronics & Gadgets", icon: "Monitor", description: "Tech, computers, and gadgets" },
  { id: "clothing", name: "Clothing & Fashion", icon: "Shirt", description: "Fashion, apparel, and accessories" },
  { id: "home", name: "Home & Furniture", icon: "Home", description: "Home decor and furniture" },
  { id: "books", name: "Books & Media", icon: "Book", description: "Books, textbooks, and media" },
  { id: "sports", name: "Sports & Outdoors", icon: "Mountain", description: "Sports gear and outdoor equipment" },
  { id: "beauty", name: "Beauty & Personal Care", icon: "Sparkles", description: "Cosmetics and personal care" },
  { id: "food", name: "Food & Groceries", icon: "ShoppingBag", description: "Groceries and household items" },
  { id: "auto", name: "Automotive", icon: "Car", description: "Car parts and automotive supplies" },
  { id: "tools", name: "Tools & Hardware", icon: "Hammer", description: "Tools, hardware, and supplies" },
  { id: "games", name: "Games & Steam Keys", icon: "Gamepad2", description: "Video games and digital keys" },
  { id: "general", name: "General Retail", icon: "Store", description: "General merchandise and retail" },
  { id: "deals", name: "Deals & Coupons", icon: "Tag", description: "Coupons, deals, and promotions" }
] as const;

type CategoryValue = typeof CATEGORIES[number]["id"];

const IconComponent = ({
  name,
  className,
}: {
  name: string;
  className?: string;
}) => {
  const iconProps = { className: className || "h-4 w-4" };

  switch (name) {
    case "Star": return <Star {...iconProps} />;
    case "Monitor": return <Monitor {...iconProps} />;
    case "Shirt": return <Shirt {...iconProps} />;
    case "Home": return <Home {...iconProps} />;
    case "Book": return <Book {...iconProps} />;
    case "Mountain": return <Mountain {...iconProps} />;
    case "Sparkles": return <Sparkles {...iconProps} />;
    case "ShoppingBag": return <ShoppingBag {...iconProps} />;
    case "Car": return <Car {...iconProps} />;
    case "Hammer": return <Hammer {...iconProps} />;
    case "Gamepad2": return <Gamepad2 {...iconProps} />;
    case "Store": return <Store {...iconProps} />;
    case "Tag": return <Tag {...iconProps} />;
    case "BarChart3": return <BarChart3 {...iconProps} />;
    case "Gift": return <Gift {...iconProps} />;
    case "Download": return <Download {...iconProps} />;
    default: return <ShoppingCart {...iconProps} />;
  }
};

function CheapLifePageContent() {
  const [activeCategory, setActiveCategory] = useQueryParam<CategoryValue>("category", {
    parse: (raw) => (raw ?? "featured") as CategoryValue,
    serialize: (v) => v ?? null,
    defaultValue: "featured",
    replace: true,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<"rating" | "name" | "popularity">("rating");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [minRating, setMinRating] = useState<number>(0);
  const [freeShippingOnly, setFreeShippingOnly] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showFavorites, setShowFavorites] = useState(false);

  // Load favorites from localStorage on mount
  React.useEffect(() => {
    const savedFavorites = localStorage.getItem('cheap-life-favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Save favorites to localStorage
  React.useEffect(() => {
    localStorage.setItem('cheap-life-favorites', JSON.stringify(favorites));
  }, [favorites]);

  const toggleFavorite = (siteId: string) => {
    setFavorites(prev =>
      prev.includes(siteId)
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  const sortSites = (sites: typeof cheapLifeSites) => {
    return [...sites].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case "rating":
          aValue = a.rating || 0;
          bValue = b.rating || 0;
          break;
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "popularity":
          // Simulate popularity based on rating and free shipping
          aValue = (a.rating || 0) + (a.freeShipping ? 1 : 0) + (favorites.includes(a.id) ? 2 : 0);
          bValue = (b.rating || 0) + (b.freeShipping ? 1 : 0) + (favorites.includes(b.id) ? 2 : 0);
          break;
        default:
          return 0;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  const getFilteredSites = () => {
    let sites = showFavorites
      ? cheapLifeSites.filter(site => favorites.includes(site.id))
      : activeCategory === "featured"
        ? getFeaturedSites()
        : getSitesByCategory(activeCategory!);

    // Apply search filter
    sites = sites.filter(site =>
      site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Apply advanced filters
    if (minRating > 0) {
      sites = sites.filter(site => (site.rating || 0) >= minRating);
    }
    if (freeShippingOnly) {
      sites = sites.filter(site => site.freeShipping);
    }

    // Apply sorting
    return sortSites(sites);
  };

  const filteredSites = getFilteredSites();

  // Statistics
  const stats = React.useMemo(() => {
    const allSites = cheapLifeSites;
    return {
      totalSites: allSites.length,
      avgRating: allSites.reduce((sum, site) => sum + (site.rating || 0), 0) / allSites.length,
      freeShippingCount: allSites.filter(site => site.freeShipping).length,
      favoriteCount: favorites.length,
      topRatedCount: allSites.filter(site => (site.rating || 0) >= 4.5).length
    };
  }, [favorites]);

  const renderStars = (rating?: number) => {
    if (!rating) return null;

    return (
      <div className="flex items-center gap-1">
        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
        <span className="text-xs text-muted-foreground">{rating}</span>
      </div>
    );
  };

  const SiteCard = ({ site }: { site: typeof cheapLifeSites[0] }) => (
    <Card className="card-enhanced hover-lift group relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg group-hover:text-primary transition-colors">
              {site.name}
            </CardTitle>
            <CardDescription className="mt-1 line-clamp-2">
              {site.description}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-2 ml-4">
            <button
              onClick={(e) => {
                e.preventDefault();
                toggleFavorite(site.id);
              }}
              className="p-1 rounded-full hover:bg-muted transition-colors"
            >
              <Heart
                className={`h-4 w-4 ${
                  favorites.includes(site.id)
                    ? "fill-red-500 text-red-500"
                    : "text-muted-foreground hover:text-red-500"
                }`}
              />
            </button>
            {renderStars(site.rating)}
            <div className="flex gap-1">
              {site.freeShipping && (
                <Badge variant="secondary" className="text-xs">
                  <Truck className="h-3 w-3 mr-1" />
                  Free Ship
                </Badge>
              )}
              {site.affiliate && (
                <Badge variant="outline" className="text-xs">
                  <Award className="h-3 w-3 mr-1" />
                  Affiliate
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <ul className="space-y-1">
            {site.features.slice(0, 3).map((feature, i) => (
              <li key={i} className="flex items-center text-sm">
                <div className="mr-2 h-1 w-1 rounded-full bg-primary"></div>
                <span className="text-muted-foreground">{feature}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <Button
              asChild
              className="flex-1 btn-enhanced hover-lift"
              size="sm"
            >
              <a
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2"
              >
                Visit Site
                <ExternalLink className="h-3 w-3" />
              </a>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigator.share?.({
                title: site.name,
                text: site.description,
                url: site.url
              })}
              className="px-3"
            >
              <Share2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );


  const currentCategory = CATEGORIES.find(cat => cat.id === activeCategory);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 animate-fade-in">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">
              Cheap Life Directory
            </h1>
            <p className="text-muted-foreground text-lg animate-slide-in-right max-w-3xl mx-auto">
              Discover the best places to find amazing deals and save money on everything you need.
              From electronics to fashion, home goods to groceries - we've curated the ultimate guide
              to living the cheap life.
            </p>
          </div>

          {/* Search, Filters, and Controls */}
          <div className="space-y-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search sites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Filters
                </Button>
                <Button
                  variant={showFavorites ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowFavorites(!showFavorites)}
                  className="flex items-center gap-2"
                >
                  <Heart className={`h-4 w-4 ${showFavorites ? "fill-current" : ""}`} />
                  Favorites ({favorites.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const data = filteredSites.map(site => `${site.name}: ${site.url}`).join('\n');
                    navigator.clipboard.writeText(data);
                  }}
                  className="flex items-center gap-2"
                >
                  <DownloadIcon className="h-4 w-4" />
                  Export
                </Button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <Card className="card-enhanced">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Sort by</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                        className="w-full p-2 border border-input rounded-lg bg-background text-sm"
                      >
                        <option value="rating">Rating</option>
                        <option value="name">Name</option>
                        <option value="popularity">Popularity</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Order</label>
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as typeof sortOrder)}
                        className="w-full p-2 border border-input rounded-lg bg-background text-sm"
                      >
                        <option value="desc">Highest First</option>
                        <option value="asc">Lowest First</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Minimum Rating</label>
                      <select
                        value={minRating}
                        onChange={(e) => setMinRating(Number(e.target.value))}
                        className="w-full p-2 border border-input rounded-lg bg-background text-sm"
                      >
                        <option value={0}>All Ratings</option>
                        <option value={3}>3+ Stars</option>
                        <option value={4}>4+ Stars</option>
                        <option value={4.5}>4.5+ Stars</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <input
                      type="checkbox"
                      id="freeShipping"
                      checked={freeShippingOnly}
                      onChange={(e) => setFreeShippingOnly(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="freeShipping" className="text-sm">Free shipping only</label>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Statistics Dashboard */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="card-enhanced">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-primary">{stats.totalSites}</div>
                  <div className="text-sm text-muted-foreground">Total Sites</div>
                </CardContent>
              </Card>
              <Card className="card-enhanced">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-primary">{stats.avgRating.toFixed(1)}</div>
                  <div className="text-sm text-muted-foreground">Avg Rating</div>
                </CardContent>
              </Card>
              <Card className="card-enhanced">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-primary">{stats.freeShippingCount}</div>
                  <div className="text-sm text-muted-foreground">Free Shipping</div>
                </CardContent>
              </Card>
              <Card className="card-enhanced">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-primary">{stats.favoriteCount}</div>
                  <div className="text-sm text-muted-foreground">Favorites</div>
                </CardContent>
              </Card>
              <Card className="card-enhanced">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-primary">{stats.topRatedCount}</div>
                  <div className="text-sm text-muted-foreground">Top Rated (4.5+)</div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar Navigation */}
            <div className="w-full lg:w-80 shrink-0">
              <Card className="card-enhanced lg:sticky lg:top-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Categories</CardTitle>
                  <CardDescription>
                    Browse deals by category
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[400px] lg:h-[600px]">
                    <div className="p-4 space-y-6">
                      {/* Categories */}
                      <div>
                        <h3 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wide">
                          Categories
                        </h3>
                        <div className="space-y-1">
                          {CATEGORIES.map((category) => (
                            <button
                              key={category.id}
                              onClick={() => setActiveCategory(category.id)}
                              className={`w-full text-left p-3 rounded-lg transition-colors hover:bg-muted/50 ${
                                activeCategory === category.id
                                  ? "bg-primary/10 border border-primary/20 text-primary"
                                  : "text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <IconComponent
                                  name={category.icon}
                                  className={`h-5 w-5 ${
                                    activeCategory === category.id ? "text-primary" : ""
                                  }`}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">
                                    {category.name}
                                  </div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {category.description}
                                  </div>
                                </div>
                                <Badge variant="secondary" className="text-xs ml-auto">
                                  {category.id === "featured"
                                    ? getFeaturedSites().length
                                    : getSitesByCategory(category.id).length}
                                </Badge>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Trending Sites */}
                      <div>
                        <h3 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          Trending
                        </h3>
                        <div className="space-y-2">
                          {[
                            { name: "GG.deals", category: "games", icon: "Search" },
                            { name: "Instant Gaming", category: "games", icon: "Zap" },
                            { name: "AllKeyShop", category: "games", icon: "BarChart3" },
                            { name: "Eneba", category: "games", icon: "Key" }
                          ].map((site) => (
                            <button
                              key={site.name}
                              onClick={() => {
                                setActiveCategory(site.category as CategoryValue);
                                setSearchQuery(site.name);
                              }}
                              className="w-full text-left p-2 rounded-lg hover:bg-muted/50 transition-colors group"
                            >
                              <div className="flex items-center gap-2">
                                <IconComponent name={site.icon} className="h-4 w-4 text-primary" />
                                <span className="text-sm truncate group-hover:text-primary transition-colors">
                                  {site.name}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <div>
                        <h3 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Stats
                        </h3>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total Sites</span>
                            <span className="font-medium">{stats.totalSites}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Free Shipping</span>
                            <span className="font-medium">{stats.freeShippingCount}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Top Rated</span>
                            <span className="font-medium">{stats.topRatedCount}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Your Favorites</span>
                            <span className="font-medium text-primary">{stats.favoriteCount}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="space-y-6">
                {/* Category Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <IconComponent
                      name={currentCategory?.icon || "ShoppingCart"}
                      className="h-6 w-6 text-primary"
                    />
                    <div>
                      <h2 className="text-2xl font-semibold">
                        {showFavorites
                          ? "My Favorites"
                          : currentCategory?.name || "All Categories"
                        }
                      </h2>
                      <p className="text-muted-foreground">
                        {showFavorites
                          ? "Your saved favorite shopping destinations"
                          : currentCategory?.description || "Browse all shopping deals"
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {searchQuery && (
                      <Badge variant="secondary" className="text-xs">
                        Search: "{searchQuery}"
                      </Badge>
                    )}
                    {(minRating > 0 || freeShippingOnly || sortBy !== "rating") && (
                      <Badge variant="outline" className="text-xs">
                        Filtered
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-sm">
                      {filteredSites.length} site{filteredSites.length !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                </div>

                {/* Quick Access - Popular Sites */}
                {!showFavorites && activeCategory === "featured" && !searchQuery && (
                  <Card className="card-enhanced">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Zap className="h-5 w-5 text-primary" />
                        Quick Access
                      </CardTitle>
                      <CardDescription>
                        Popular sites you might be interested in
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { name: "Steam", url: "https://store.steampowered.com", icon: "Gamepad2" },
                          { name: "Costco", url: "https://costco.com", icon: "Package" },
                          { name: "AliExpress", url: "https://aliexpress.com", icon: "Globe" },
                          { name: "GG.deals", url: "https://gg.deals", icon: "Search" }
                        ].map((site) => (
                          <a
                            key={site.name}
                            href={site.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col items-center p-4 rounded-lg border hover:bg-muted/50 transition-colors group"
                          >
                            <IconComponent name={site.icon} className="h-6 w-6 text-primary mb-2 group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-medium text-center">{site.name}</span>
                          </a>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Separator />

                {/* Sites Grid */}
                {filteredSites.length > 0 ? (
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                    {filteredSites.map((site) => (
                      <SiteCard key={site.id} site={site} />
                    ))}
                  </div>
                ) : (
                  <Card className="card-enhanced">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <div className="text-muted-foreground mb-4">
                        <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="text-center">
                          {searchQuery
                            ? `No sites found matching "${searchQuery}"`
                            : "No sites found in this category yet."
                          }
                        </p>
                        <p className="text-center text-sm mt-2">
                          {searchQuery
                            ? "Try adjusting your search terms"
                            : "Check back soon for new additions!"
                          }
                        </p>
                      </div>
                      {searchQuery && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSearchQuery("")}
                          className="mt-4"
                        >
                          Clear Search
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Tips Section */}
                <Card className="card-enhanced mt-8">
                  <CardHeader>
                    <CardTitle>Shopping Tips</CardTitle>
                    <CardDescription>
                      Save money and shop smarter with these strategies
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                        <Truck className="h-6 w-6 text-primary mt-0.5 shrink-0" />
                        <div>
                          <h3 className="font-semibold mb-1">Free Shipping</h3>
                          <p className="text-sm text-muted-foreground">
                            Many sites offer free shipping on orders over certain amounts. Look for the free shipping badge.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                        <Star className="h-6 w-6 text-primary mt-0.5 shrink-0" />
                        <div>
                          <h3 className="font-semibold mb-1">Quality Products</h3>
                          <p className="text-sm text-muted-foreground">
                            Find high-quality items at a fraction of retail prices from trusted retailers.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                        <Award className="h-6 w-6 text-primary mt-0.5 shrink-0" />
                        <div>
                          <h3 className="font-semibold mb-1">Trusted Sellers</h3>
                          <p className="text-sm text-muted-foreground">
                            Shop from reputable retailers with good return policies and customer support.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                        <ExternalLink className="h-6 w-6 text-primary mt-0.5 shrink-0" />
                        <div>
                          <h3 className="font-semibold mb-1">Compare Prices</h3>
                          <p className="text-sm text-muted-foreground">
                            Use multiple sites to compare prices and find the best deals available.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CheapLifePage() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 py-8">Loading...</div>}>
      <CheapLifePageContent />
    </Suspense>
  );
}
